import { useEffect, useRef, useState } from 'react';
import { IconButton } from '@clay-run/glaze/iconbutton';
import { Text } from '@clay-run/glaze/text';
import { PaperPlaneTilt } from '@phosphor-icons/react/dist/icons/PaperPlaneTilt';
import { AGENT_MAX_MESSAGES, type ChatMessages } from '../types';

export type ChatMessage = { id: number; role: 'user' | 'assistant'; text: string; choices: string[] | null };

/**
 * Conversation state for an agent chat. `request` gets the recent history (capped at what the
 * server accepts) and calls `onReply` with the agent's answer.
 */
export function useChatSession({
  opening,
  isPending,
  request,
}: {
  opening: ChatMessage[];
  isPending: boolean;
  request: (history: ChatMessages, onReply: (text: string, choices: string[] | null) => void) => void;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>(opening);

  const sendAfter = (history: ChatMessage[], text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isPending) return;
    const next = [...history, { id: history.length, role: 'user' as const, text: trimmed, choices: null }];
    setMessages(next);
    request(next.slice(-AGENT_MAX_MESSAGES).map(m => ({ role: m.role, text: m.text })), (reply, choices) =>
      setMessages(prev => [...prev, { id: prev.length, role: 'assistant', text: reply, choices }]),
    );
  };

  return {
    messages,
    send: (text: string) => sendAfter(messages, text),
    retry: () => {
      const last = messages[messages.length - 1];
      if (last?.role === 'user') sendAfter(messages.slice(0, -1), last.text);
    },
  };
}

function Bubble({ message }: { message: ChatMessage }) {
  return message.role === 'assistant' ? (
    <div className="max-w-[88%] self-start whitespace-pre-line rounded-2xl rounded-bl-sm bg-bg-secondary px-4 py-2.5 text-sm leading-relaxed">
      {message.text}
    </div>
  ) : (
    <div className="max-w-[85%] self-end whitespace-pre-line rounded-2xl rounded-br-sm bg-bg-inverse px-4 py-2.5 text-sm leading-relaxed text-content-inverse">
      {message.text}
    </div>
  );
}

function Choices({ options, onSelect, disabled }: { options: string[]; onSelect: (o: string) => void; disabled: boolean }) {
  return (
    <div className="flex flex-wrap gap-2 pl-1">
      {options.map(option => (
        <button
          key={option}
          type="button"
          disabled={disabled}
          onClick={() => onSelect(option)}
          className="min-h-8 rounded-full border border-border-primary bg-bg-primary px-3 text-sm hover:bg-bg-primary-hover disabled:opacity-50"
        >
          {option}
        </button>
      ))}
    </div>
  );
}

export function ChatThread({
  messages,
  isPending,
  error,
  onSend,
  onRetry,
  inputId,
  placeholder,
  pendingLabel,
  accessory,
}: {
  messages: ChatMessage[];
  isPending: boolean;
  error: Error | null;
  onSend: (text: string) => void;
  onRetry: () => void;
  inputId: string;
  placeholder: string;
  pendingLabel: string;
  accessory?: React.ReactNode;
}) {
  const [reply, setReply] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const last = messages[messages.length - 1];

  // Keep the newest message in view; the scroll container is a DOM node outside React state.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, isPending]);

  const send = (text: string) => {
    if (!text.trim() || isPending) return;
    onSend(text);
    setReply('');
  };

  return (
    <>
      <div ref={scrollRef} className="flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto p-4">
        {messages.map(message => (
          <Bubble key={message.id} message={message} />
        ))}
        {last?.role === 'assistant' && last.choices && <Choices options={last.choices} onSelect={send} disabled={isPending} />}
        {isPending && (
          <Text size="sm" variant="tertiary">
            {pendingLabel}
          </Text>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-text-red">{error.message}</span>
            <button type="button" className="underline" onClick={onRetry}>
              Retry
            </button>
          </div>
        )}
      </div>

      <form
        className="flex items-end gap-2 border-t border-border-primary p-3"
        onSubmit={e => {
          e.preventDefault();
          send(reply);
        }}
      >
        {accessory}
        <label htmlFor={inputId} className="sr-only">
          {placeholder}
        </label>
        <textarea
          id={inputId}
          rows={1}
          value={reply}
          onChange={e => setReply(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send(reply);
            }
          }}
          placeholder={placeholder}
          className="min-h-9 flex-1 resize-none rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm"
        />
        <IconButton icon={PaperPlaneTilt} aria-label="Send" variant="primary" type="submit" disabled={isPending || !reply.trim()} />
      </form>
    </>
  );
}
